import { FC, useEffect, useState, useRef, useContext } from "react";
import { useSelector } from "react-redux";
import Notifications from "../notifications/Notifications";
import Logo from "../logo/Logo";
import searchSvg from "../../shared/images/icons/search.svg";
import messageSvg from "../../shared/images/icons/message.svg";
import {
  StyledHeader,
  StyledLogo,
  StyledNavbarWrap,
  StyleNavbarRight,
  StyledNavbarElement,
  StyledSearchBox,
  NAV_ELEMENT_MARGINS,
  StyledSearchResultItem,
  StyledHeaderActions,
  StyledHeaderActionSlot,
  StyledTextField,
  StyledSearchResults,
  StyledSearchAdornment,
  StyledIconDivider,
} from "./style";
import { InputAdornment, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import { RootState } from "../../redux/reducers";
import ProfileDropdown from "../../components/header/profile-dropdown";
import { NavCandidateListItem } from "../../redux/actions/actionTypes";
import axios from "axios";
import { SERVICEURL } from "../../appconfig";
import { ThemeContext } from "../../context/ThemeContextProvider";

interface IProps {
  hasSidebar?: boolean;
  sidebarLayout?: 1 | 2;
}

const Nav: FC<IProps> = () => {
  const sidebarOpen = useSelector((state: RootState) => state.ui.sidebar);
  const [navCandidateSearchList, setNavCandidateSearchList] = useState<
    NavCandidateListItem[]
  >([]);

  const [searchValue, setSearchValue] = useState<string>("");
  const [showResults, setShowResults] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const history = useHistory();
  const { darkModeActive } = useContext(ThemeContext);
  const searchMsgColor = darkModeActive ? "var(--w-text-muted)" : undefined;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (searchRef.current && target && !searchRef.current.contains(target)) {
        setShowResults(false);
        setSearchValue("");
        setNavCandidateSearchList([]);
        setIsLoading(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchValue === "") {
      setShowResults(false);
      setNavCandidateSearchList([]);
    }
  }, [searchValue]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  useEffect(() => {
    const term = searchValue.trim();

    if (term.length < 3) {
      setIsLoading(false);
      setNavCandidateSearchList([]);
      setShowResults(false);
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      return;
    }

    const timer = setTimeout(() => {
      if (controllerRef.current) controllerRef.current.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      const mytoken = localStorage.getItem("token");
      const HEADER = {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + mytoken,
        },
        signal: controller.signal as any,
      } as any;

      const input = { NameSurname: term } as const;
      setIsLoading(true);
      axios
        .post(`${SERVICEURL}api/Nav/NavSearch`, input, HEADER)
        .then((response) => {
          const data = response.data;
          const apiItems = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data?.Items)
                ? data.Items
                : [];

          const mapped: NavCandidateListItem[] = apiItems
            .map((it: any) => ({
              Id: String(it?.id ?? it?.Id ?? "").trim(),
              NameSurname: it?.nameSurname ?? it?.NameSurname ?? null,
            }))
            .filter(
              (item: NavCandidateListItem) =>
                item.Id !== "" && item.NameSurname !== null,
            );

          setNavCandidateSearchList(mapped);
          setIsLoading(false);
          setShowResults(true);
        })
        .catch((error) => {
          console.error(`Error fetching data:`, error);
          setNavCandidateSearchList([]);
          setIsLoading(false);
          setShowResults(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleResultClick = (item: NavCandidateListItem) => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setSearchValue("");
    setShowResults(false);
    setNavCandidateSearchList([]);
    setIsLoading(false);
    history.push(`/candidate-detail/${item.Id}`);
  };

  return (
    <>
      <StyledHeader $sidebarOpen={sidebarOpen}>
        <StyledLogo>
          <Logo />
        </StyledLogo>
        <StyledNavbarWrap>
          <StyledSearchBox ref={searchRef}>
            <StyledTextField
              fullWidth
              variant="standard"
              placeholder="Ara"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => {
                if (navCandidateSearchList.length > 0) {
                  setShowResults(true);
                }
              }}
              onBlur={(e) => {
                const currentTarget = e.currentTarget;
                setTimeout(() => {
                  const activeElement = document.activeElement;
                  if (!searchRef.current?.contains(activeElement)) {
                    setShowResults(false);
                  }
                }, 300);
              }}
              InputProps={{}}
            />
            <StyledSearchAdornment>
              <img
                src={searchSvg}
                alt="search"
                width={30}
                height={30}
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </StyledSearchAdornment>
            {showResults &&
              (() => {
                return (
                  <StyledSearchResults>
                    {isLoading ? (
                      <Typography
                        variant="body2"
                        color={searchMsgColor ? undefined : "text.secondary"}
                        sx={searchMsgColor ? { p: 2, color: searchMsgColor } : { p: 2 }}
                      >
                        Aranıyor...
                      </Typography>
                    ) : navCandidateSearchList.length > 0 ? (
                      navCandidateSearchList.map(
                        (item: NavCandidateListItem) => (
                          <StyledSearchResultItem
                            key={item.Id}
                            onClick={() => handleResultClick(item)}
                          >
                            <Typography variant="body2">
                              {item.NameSurname && item.NameSurname.length > 50
                                ? `${item.NameSurname.substring(0, 50)}...`
                                : item.NameSurname || "İsimsiz"}
                            </Typography>
                          </StyledSearchResultItem>
                        ),
                      )
                    ) : (
                      <Typography
                        variant="body2"
                        color={searchMsgColor ? undefined : "text.secondary"}
                        sx={searchMsgColor ? { p: 2, color: searchMsgColor } : { p: 2 }}
                      >
                        Bu isme ait aday bulunamadı.
                      </Typography>
                    )}
                  </StyledSearchResults>
                );
              })()}
          </StyledSearchBox>
        </StyledNavbarWrap>

        <StyleNavbarRight>
          <StyledNavbarElement ml={NAV_ELEMENT_MARGINS.default}>
            <StyledHeaderActions>
              <StyledHeaderActionSlot>
                <img
                  src={messageSvg}
                  alt="message"
                  style={{ cursor: "pointer" }}
                />
              </StyledHeaderActionSlot>
              <StyledIconDivider />
              <StyledHeaderActionSlot>
                <Notifications />
              </StyledHeaderActionSlot>
              <StyledIconDivider />
              <StyledHeaderActionSlot $alignStart>
                <ProfileDropdown />
              </StyledHeaderActionSlot>
            </StyledHeaderActions>
          </StyledNavbarElement>
        </StyleNavbarRight>
      </StyledHeader>
    </>
  );
};

Nav.defaultProps = {
  sidebarLayout: 1,
};

export default Nav;
